/**
 * Constraint processing utilities
 * Handles redundancy detection and removal for integer programming problems
 */

import { IntegerProblem } from './cuts'
import { SimplexSolver } from './simplex'

const coreDebugEnabled =
  typeof globalThis !== 'undefined' &&
  Boolean((globalThis as { __SCULPTOR_DEBUG_CORE__?: boolean }).__SCULPTOR_DEBUG_CORE__)

const coreLog = (...args: unknown[]): void => {
  if (coreDebugEnabled) {
    console.log(...args)
  }
}

const coreWarn = (...args: unknown[]): void => {
  if (coreDebugEnabled) {
    console.warn(...args)
  }
}

/**
 * Result of redundancy analysis
 */
export interface RedundancyAnalysis {
  redundantIndices: number[]
  essentialIndices: number[]
  removedCount: number
  originalCount: number
}

/**
 * Check if a constraint represents a variable lower bound (e.g., x >= 0)
 * These constraints should be preserved as they define the domain
 */
function isVariableLowerBound(
  constraintCoeffs: number[],
  rhs: number,
  tolerance: number = 1e-10
): boolean {
  // Check if constraint has exactly one non-zero coefficient of ±1
  let nonZeroCount = 0
  let nonZeroValue = 0
  
  for (let i = 0; i < constraintCoeffs.length; i++) {
    if (Math.abs(constraintCoeffs[i]) > tolerance) {
      nonZeroCount++
      nonZeroValue = constraintCoeffs[i]
    }
  }
  
  // Must have exactly one non-zero coefficient
  if (nonZeroCount !== 1) {
    return false
  }
  
  // Check if it's a lower bound: -x ≤ 0 (i.e., x ≥ 0) or similar
  if (Math.abs(nonZeroValue + 1) < tolerance && Math.abs(rhs) < tolerance) {
    return true // -x ≤ 0, i.e., x ≥ 0
  }
  
  // Could also check for other forms like x ≤ M for large M, but for now
  // we focus on the common case of non-negativity constraints
  return false
}

/**
 * Check if a constraint is redundant using the standard LP-based method
 * 
 * To test if constraint k: α_k^T x ≤ β_k is redundant, we solve:
 * p_k* = max{α_k^T x} subject to: α_i^T x ≤ β_i for all i ≠ k, x ≥ 0
 * 
 * If p_k* ≤ β_k, then constraint k is redundant.
 * If p_k* > β_k, then constraint k is essential.
 */
export function isConstraintRedundant(
  problem: IntegerProblem,
  constraintIndex: number,
  tolerance: number = 1e-8
): boolean {
  // Get the constraint to test: α_k^T x ≤ β_k
  const alpha_k = problem.A[constraintIndex]
  const beta_k = problem.b[constraintIndex]
  
  // Always preserve variable lower bounds (e.g., x ≥ 0)
  if (isVariableLowerBound(alpha_k, beta_k, tolerance)) {
    return false // Never consider lower bounds as redundant
  }
  
  // Create reduced problem without constraint k
  const otherConstraints = problem.A
    .map((row, i) => ({ row, rhs: problem.b[i], index: i }))
    .filter(({ index }) => index !== constraintIndex)
  
  if (otherConstraints.length === 0) {
    return false // Cannot remove all constraints
  }
  
  try {
    // Standard LP-based redundancy detection:
    // To test if constraint k: α_k^T x ≤ β_k is redundant, solve:
    // p_k* = max{α_k^T x} subject to: α_i^T x ≤ β_i for all i ≠ k, x ≥ 0
    // If p_k* ≤ β_k, then constraint k is redundant
    // If p_k* > β_k, then constraint k is essential
    
    // Construct constraint matrix A and RHS vector b for other constraints
    const A_other = otherConstraints.map(({ row }) => row)
    const b_other = otherConstraints.map(({ rhs }) => rhs)
    
    // Objective function: maximize α_k^T x
    const c = alpha_k.slice() // Copy the coefficients
    
    // Solve the LP problem using our SimplexSolver
    const result = SimplexSolver.getOptimalTableau(A_other, b_other, c)
    
    if (!result.isOptimal) {
      // If not optimal (could be infeasible or unbounded)
      // Check if it's unbounded by looking at the objective value
      const objValue = result.objectiveValue.toNumber()
      if (objValue === Infinity || objValue > 1e10) {
        // Unbounded: α_k^T x can be arbitrarily large
        // This means the constraint being tested is essential
        return false
      } else {
        // Likely infeasible: the remaining constraints have no solution
        // This means the constraint being tested is essential
        return false
      }
    }
    
    // Get the optimal value p_k*
    const p_k_star = result.objectiveValue.toNumber()
    
    // If p_k* ≤ β_k, then constraint k is redundant
    const isRedundant = p_k_star <= beta_k + tolerance
    
    return isRedundant
    
  } catch (error) {
    coreWarn(`Error checking redundancy for constraint ${constraintIndex}:`, error)
    // On error, assume constraint is essential (safer)
    return false
  }
}

/**
 * Remove redundant constraints from a problem
 */
export function removeRedundantConstraints(
  problem: IntegerProblem,
  tolerance: number = 1e-8
): { cleanedProblem: IntegerProblem, analysis: RedundancyAnalysis } {
  coreLog('Starting redundancy removal...')
  
  const originalCount = problem.A.length
  const redundantIndices: number[] = []
  const essentialIndices: number[] = []
  
  // For 3D problems, we need at least 4 constraints to form a bounded polyhedron
  // More generally, we need at least n+1 constraints for n variables
  const minConstraints = Math.max(4, problem.c.length + 1)
  
  // Check each constraint for redundancy
  for (let i = 0; i < problem.A.length; i++) {
    coreLog(`Checking constraint ${i}/${problem.A.length}...`)
    
    // Don't remove constraint if we're already at minimum
    const remainingAfterRemoval = originalCount - redundantIndices.length - 1
    if (remainingAfterRemoval < minConstraints) {
      essentialIndices.push(i)
      coreLog(`Constraint ${i} is essential (minimum constraint limit)`)
      continue
    }
    
    if (isConstraintRedundant(problem, i, tolerance)) {
      redundantIndices.push(i)
      coreLog(`Constraint ${i} is redundant`)
    } else {
      essentialIndices.push(i)
      coreLog(`Constraint ${i} is essential`)
    }
  }
  
  // Create cleaned problem
  const cleanedA = problem.A.filter((_, i) => !redundantIndices.includes(i))
  const cleanedB = problem.b.filter((_, i) => !redundantIndices.includes(i))
  const cleanedInequalities = problem.inequalities.filter((_, i) => !redundantIndices.includes(i))
  
  const cleanedProblem: IntegerProblem = {
    A: cleanedA,
    b: cleanedB,
    c: problem.c,
    inequalities: cleanedInequalities
  }
  
  const analysis: RedundancyAnalysis = {
    redundantIndices,
    essentialIndices,
    removedCount: redundantIndices.length,
    originalCount
  }
  
  coreLog(`Redundancy removal complete: ${analysis.removedCount}/${originalCount} constraints removed`)
  
  return { cleanedProblem, analysis }
}

/**
 * Construct standard form by adding slack variables
 * (Helper function for redundancy checking)
 */
/**
 * Check for obviously redundant constraints before LP solving
 * (faster heuristic checks)
 */
export function detectObviousRedundancy(problem: IntegerProblem): number[] {
  const redundantIndices: number[] = []
  
  // Check for duplicate constraints
  for (let i = 0; i < problem.A.length; i++) {
    for (let j = i + 1; j < problem.A.length; j++) {
      if (areConstraintsEqual(problem.A[i], problem.b[i], problem.A[j], problem.b[j])) {
        redundantIndices.push(j) // Remove the later one
      }
    }
  }
  
  // Check for constraints that are clearly dominated
  for (let i = 0; i < problem.A.length; i++) {
    for (let j = 0; j < problem.A.length; j++) {
      if (i !== j && isConstraintDominated(problem.A[i], problem.b[i], problem.A[j], problem.b[j])) {
        redundantIndices.push(i) // Remove the dominated one
      }
    }
  }
  
  return [...new Set(redundantIndices)] // Remove duplicates
}

/**
 * Check if two constraints are equal
 */
function areConstraintsEqual(
  alpha1: number[], beta1: number,
  alpha2: number[], beta2: number,
  tolerance: number = 1e-10
): boolean {
  if (alpha1.length !== alpha2.length) return false
  if (Math.abs(beta1 - beta2) > tolerance) return false
  
  for (let i = 0; i < alpha1.length; i++) {
    if (Math.abs(alpha1[i] - alpha2[i]) > tolerance) return false
  }
  
  return true
}

/**
 * Check if constraint 1 is dominated by constraint 2
 * (constraint 2 is tighter and makes constraint 1 redundant)
 */
function isConstraintDominated(
  alpha1: number[], beta1: number,
  alpha2: number[], beta2: number,
  tolerance: number = 1e-10
): boolean {
  if (alpha1.length !== alpha2.length) return false
  
  // Check if alpha1 = k * alpha2 for some k > 0
  let k = 0
  let foundNonZero = false
  
  for (let i = 0; i < alpha1.length; i++) {
    if (Math.abs(alpha2[i]) > tolerance) {
      const ratio = alpha1[i] / alpha2[i]
      if (!foundNonZero) {
        k = ratio
        foundNonZero = true
      } else if (Math.abs(ratio - k) > tolerance) {
        return false // Not proportional
      }
    } else if (Math.abs(alpha1[i]) > tolerance) {
      return false // alpha1 has non-zero where alpha2 is zero
    }
  }
  
  if (!foundNonZero || k <= tolerance) return false
  
  // Check if beta1 >= k * beta2 (constraint 1 is looser)
  return beta1 >= k * beta2 - tolerance
}

/**
 * Fast preprocessing that combines obvious redundancy detection
 * with basic feasibility checks
 */
export function preprocessConstraints(
  problem: IntegerProblem,
  options: {
    removeObviousRedundancy?: boolean
    checkFullRedundancy?: boolean
    tolerance?: number
  } = {}
): { cleanedProblem: IntegerProblem, analysis: RedundancyAnalysis } {
  const {
    removeObviousRedundancy = true,
    checkFullRedundancy = true,
    tolerance = 1e-8
  } = options
  
  coreLog('Preprocessing constraints...')
  
  let currentProblem = problem
  let totalRemoved = 0
  const allRedundantIndices: number[] = []
  
  // Step 1: Remove obviously redundant constraints
  if (removeObviousRedundancy) {
    const obviousRedundant = detectObviousRedundancy(currentProblem)
    if (obviousRedundant.length > 0) {
      coreLog(`Found ${obviousRedundant.length} obviously redundant constraints`)
      
      const newA = currentProblem.A.filter((_, i) => !obviousRedundant.includes(i))
      const newB = currentProblem.b.filter((_, i) => !obviousRedundant.includes(i))
      const newInequalities = currentProblem.inequalities.filter((_, i) => !obviousRedundant.includes(i))
      
      currentProblem = {
        A: newA,
        b: newB,
        c: currentProblem.c,
        inequalities: newInequalities
      }
      
      allRedundantIndices.push(...obviousRedundant)
      totalRemoved += obviousRedundant.length
    }
  }
  
  // Step 2: Full redundancy check using LP
  if (checkFullRedundancy && currentProblem.A.length > 0) {
    const { cleanedProblem, analysis } = removeRedundantConstraints(currentProblem, tolerance)
    currentProblem = cleanedProblem
    totalRemoved += analysis.removedCount
  }
  
  const finalAnalysis: RedundancyAnalysis = {
    redundantIndices: allRedundantIndices,
    essentialIndices: Array.from({ length: currentProblem.A.length }, (_, i) => i),
    removedCount: totalRemoved,
    originalCount: problem.A.length
  }
  
  coreLog(`Preprocessing complete: ${totalRemoved}/${problem.A.length} constraints removed`)
  
  return { cleanedProblem: currentProblem, analysis: finalAnalysis }
} 
